class UpsellBindingManager {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener(
      'cartUpdated',
      this.handleCartUpdate.bind(this)
    );

    this.initQuantitySync();
    this.updateCartCount();
  }

  async handleCartUpdate() {
    console.log('Cart updated, checking bound items...');

    await this.syncBoundItemQuantities();
    await this.updateCartCount();
  }

  initQuantitySync() {
    document.addEventListener('change', (event) => {
      const quantityInput = event.target;

      if (
        quantityInput.classList.contains('quantity__input') &&
        quantityInput.name === 'updates[]'
      ) {
        this.handleMainProductQuantityChange(quantityInput);
      }
    });
  }

  async handleMainProductQuantityChange(quantityInput) {
    const newQuantity = parseInt(quantityInput.value, 10) || 0;

    const variantId = quantityInput.getAttribute(
      'data-quantity-variant-id'
    );

    if (!variantId) return;

    try {
      const cart = await this.getCart();

      if (newQuantity === 0) {
        const remainingItems = cart.items.filter((item) => {
          if (String(item.variant_id) === String(variantId)) {
            return false;
          }

          if (item.properties?._is_hidden_upsell === 'true') {
            return false;
          }

          if (item.properties?._fast_processing === 'true') {
            return false;
          }

          return true;
        });

        const onlyGiftCardsWillRemain =
          remainingItems.length === 0 ||
          remainingItems.every(
            (item) =>
              item.product_type === 'Gift Cards' ||
              item.gift_card === true
          );

        if (onlyGiftCardsWillRemain) return;
      }

      const boundItems = this.findBoundItems(
        cart,
        variantId
      );

      if (boundItems.length) {
        await this.updateBoundItemQuantities(
          boundItems,
          newQuantity
        );
      }

      await this.updateCartCount();
    } catch (error) {
      console.warn(
        'Failed to sync bound item quantities:',
        error
      );
    }
  }

  async syncBoundItemQuantities() {
    try {
      const cart = await this.getCart();

      const onlyGiftCards =
        cart.items.length > 0 &&
        cart.items.every(
          (item) =>
            item.product_type === 'Gift Cards' ||
            item.gift_card === true
        );

      if (onlyGiftCards) return;

      const updates = {};

      cart.items.forEach((item) => {
        if (!item.properties?._upsell_of) return;

        const mainVariantId = item.properties._upsell_of;

        const mainItem = cart.items.find(
          (cartItem) =>
            String(cartItem.variant_id) ===
            String(mainVariantId)
        );

        if (mainItem) {
          if (item.quantity !== mainItem.quantity) {
            updates[item.key] = mainItem.quantity;
          }
        } else {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length) {
        await this.updateCartItems(updates);
      }
    } catch (error) {
      console.warn(
        'Failed to sync bound items:',
        error
      );
    }
  }

  findBoundItems(cart, mainVariantId) {
    return cart.items.filter((item) => {
      const boundToVariant =
        item.properties?._upsell_of;

      const isHiddenUpsell =
        item.properties?._is_hidden_upsell === 'true';

      return (
        String(boundToVariant) === String(mainVariantId) &&
        isHiddenUpsell
      );
    });
  }

  async updateBoundItemQuantities(
    boundItems,
    newQuantity
  ) {
    const updates = {};

    boundItems.forEach((item) => {
      updates[item.key] = newQuantity;
    });

    if (Object.keys(updates).length) {
      await this.updateCartItems(updates);
    }
  }

  async updateCartItems(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(
        `Cart update failed: ${response.status}`
      );
    }

    return response.json();
  }

  async getCart() {
    const response = await fetch('/cart.js', {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cart: ${response.status}`
      );
    }

    return response.json();
  }

  calculateTotalWithBoundItems(cart) {
    return cart.items.reduce(
      (total, item) => total + item.final_line_price,
      0
    );
  }

  getVisibleItemsCount(cart) {
    return cart.items.reduce((count, item) => {
      const isHiddenUpsell =
        item.properties?._is_hidden_upsell === 'true';

      const isFastProcessing =
        item.properties?._fast_processing === 'true';

      if (isHiddenUpsell || isFastProcessing) {
        return count;
      }

      return count + item.quantity;
    }, 0);
  }

  async removeBoundItems(mainVariantId) {
    try {
      const cart = await this.getCart();

      const boundItems = this.findBoundItems(
        cart,
        mainVariantId
      );

      if (!boundItems.length) return;

      const updates = {};

      boundItems.forEach((item) => {
        updates[item.key] = 0;
      });

      await this.updateCartItems(updates);
      await this.updateCartCount();
    } catch (error) {
      console.warn(
        'Failed to remove bound items:',
        error
      );
    }
  }

  async updateCartCount() {
    if (
      typeof window.helmUpdateCartCount !== 'function'
    ) {
      return;
    }

    await window.helmUpdateCartCount();
  }
}

function initUpsellBindingManager() {
  if (window.upsellBindingManager) return;

  window.upsellBindingManager =
    new UpsellBindingManager();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initUpsellBindingManager,
    { once: true }
  );
} else {
  initUpsellBindingManager();
}